/**
 * 매칭 셀 navigation floating panel — 매칭된 청크들 사이에 매칭된 셀들이 여러 개일 때,
 * 그 셀들 사이를 이동할 수 있게 해주는 panel.
 *
 * Figma:
 * - Design System 0.1 > IXD-DSD Reconciliation > Floating panel > IXD 셀 여러개인 경우
 *
 * 현재 위치한 셀(`activeIndex`)은 파란색 번호 배지로 강조되고, 나머지는 회색 outline
 * 배지로 표시된다. `onClick` 이 있는 항목은 버튼으로 렌더링되어 클릭 시 해당 셀로
 * 포커스가 이동한다.
 *
 * 헤더 chip 텍스트("라인아이템")는 Figma 디자인을 그대로 유지 — 사용자에게는
 * "라인아이템 단위로 묶인 매칭"이라는 의미로 노출된다.
 */

import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import ChipSolid from "@/toggle-ui-kit/chip/chip-solid";
import { IconButton } from "@/toggle-ui-kit/icon-button";
import { LineItemIcon } from "@/toggle-ui-kit/icon/line-item-icon";

export interface MatchNavigationItem {
  /** 표시 텍스트 (예: "자본", "시장변수 상승 시 당기손익의 증가") */
  label: string;
  /** 보조 배지 (예: "단수차 +2") — 단수차 매칭 항목에 표시 */
  badge?: string;
  /** 있으면 항목이 버튼으로 렌더링되어 클릭 가능 (대응 셀 위치로 포커스 이동) */
  onClick?: () => void;
}

export interface MatchNavigationFloatingPanelProps {
  items: MatchNavigationItem[];
  /** 현재 위치한 매칭 셀의 0-based index — 파란색 배지로 강조. 미지정 시 모두 비활성. */
  activeIndex?: number;
  onClose?: () => void;
  /** 위치/추가 스타일 (예: "fixed bottom-6 right-6 z-50") */
  className?: string;
}

// Figma Shadow/Large 정확 매핑 (cell-linkage-floating-panel 과 동일)
const SHADOW_LARGE =
  "shadow-[0_4px_8px_4px_rgba(0,0,0,0.05),0_10px_15px_-3px_rgba(0,0,0,0.1)]";

const MatchNavigationFloatingPanel = ({
  items,
  activeIndex,
  onClose,
  className,
}: MatchNavigationFloatingPanelProps) => {
  return (
    <div
      role="dialog"
      aria-label="라인아이템"
      className={cn(
        "w-[217px] overflow-hidden rounded-[8px] bg-background-base border border-line-neutral",
        SHADOW_LARGE,
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-start self-stretch border-b border-line-neutral">
        <div className="flex flex-1 p-2">
          <ChipSolid
            size="18px"
            leftIcon={
              <LineItemIcon isExtendedTag={false} className="size-2.5" />
            }
            rounded={false}
            showRightIcon={false}
            className="bg-primary-color text-static-white"
          >
            라인아이템
          </ChipSolid>
        </div>
        {onClose && (
          <div className="flex self-stretch items-center justify-center px-2">
            <IconButton
              icon={<X size={14} />}
              onClick={onClose}
              aria-label="닫기"
              className="size-6"
            />
          </div>
        )}
      </div>

      {/* Body */}
      <ol className="flex flex-col gap-3 p-2">
        {items.map((item, idx) => {
          const isActive = idx === activeIndex;
          const row = (
            <div className="flex w-full min-w-0 items-center gap-1.5">
              <div
                className={cn(
                  "flex size-4 shrink-0 items-center justify-center rounded-full caption-2-medium",
                  isActive
                    ? "bg-primary-color text-static-white"
                    : "bg-label-alternative text-static-white",
                )}
                aria-hidden
              >
                {idx + 1}
              </div>
              <span
                className={cn(
                  "min-w-0 caption-2-medium [overflow-wrap:anywhere]",
                  isActive ? "text-label-normal" : "text-label-alternative",
                )}
              >
                {item.label}
              </span>
              {item.badge && (
                <span className="inline-flex shrink-0 items-center rounded bg-yellow-200 px-1 py-0.5 caption-2-medium text-label-strong">
                  {item.badge}
                </span>
              )}
            </div>
          );
          return (
            <li key={`${idx}-${item.label}`}>
              {item.onClick ? (
                <div
                  onClick={item.onClick}
                  aria-current={isActive ? "true" : undefined}
                  className="flex w-full rounded-[4px] cursor-pointer text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2b7fff] hover:underline"
                >
                  {row}
                </div>
              ) : (
                row
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default MatchNavigationFloatingPanel;
